// ─── Stroke Utilization Shared Utility ───────────────────────────────────────
// Provides ranking logic and the shared display component for the
// "Best Stroke Utilization" section on ResultsScreen and RoundSummary.

export type StrokePlayerInput = {
  name: string;
  strokesReceived: number;
  withStrokeWins: number;
  withStrokeTies: number;
  withStrokeLosses: number;
};

export type BestStrokeUtilizationResult = {
  name: string;
  strokesReceived: number;
  withStrokeWins: number;
  withStrokeTies: number;
  withStrokeLosses: number;
  protectedHoles: number;
  utilizationRate: number;
};

/**
 * From all players who received strokes, returns the ranked list (best first).
 * Ranking:
 *  1. Highest utilizationRate  (protectedHoles / strokesReceived)
 *  2. Highest withStrokeWins
 *  3. Highest strokesReceived
 *  4. Name alphabetical
 *
 * Returns [] if no eligible players exist.
 */
export function computeBestStrokeUtilization(
  players: StrokePlayerInput[],
): BestStrokeUtilizationResult[] {
  const eligible = players
    .filter(p => p.strokesReceived > 0)
    .map(p => {
      const protectedHoles = p.withStrokeWins + p.withStrokeTies;
      const utilizationRate = protectedHoles / p.strokesReceived;
      return {
        name: p.name,
        strokesReceived: p.strokesReceived,
        withStrokeWins: p.withStrokeWins,
        withStrokeTies: p.withStrokeTies,
        withStrokeLosses: p.withStrokeLosses,
        protectedHoles,
        utilizationRate,
      };
    });

  if (eligible.length === 0) return [];

  eligible.sort((a, b) => {
    if (b.utilizationRate !== a.utilizationRate) return b.utilizationRate - a.utilizationRate;
    if (b.withStrokeWins !== a.withStrokeWins) return b.withStrokeWins - a.withStrokeWins;
    if (b.strokesReceived !== a.strokesReceived) return b.strokesReceived - a.strokesReceived;
    return a.name.localeCompare(b.name);
  });

  return eligible;
}

/**
 * Shared React section. Pass ranked[] from computeBestStrokeUtilization().
 * Renders nothing if ranked is empty.
 */
export function BestStrokeUtilizationSection({
  ranked,
}: {
  ranked: BestStrokeUtilizationResult[];
}) {
  if (ranked.length === 0) return null;

  const winner = ranked[0];
  const ratePct = Math.round(winner.utilizationRate * 100);

  return (
    <section
      className="bg-white rounded-xl border border-gray-100 p-4 space-y-2"
      data-testid="section-best-stroke-utilization"
    >
      <h2 className="text-sm font-black text-gray-900">Best Stroke Utilization</h2>

      <p
        className="text-sm font-semibold text-gray-900"
        data-testid="bsu-winner-name"
      >
        {winner.name} used strokes best today
      </p>

      <p className="text-sm text-gray-600" data-testid="bsu-stats-line">
        <span className="text-emerald-700 font-bold">{winner.withStrokeWins} won</span>
        <span className="text-gray-400"> / </span>
        <span className="font-bold text-gray-700">{winner.withStrokeTies} tied</span>
        <span className="text-gray-400"> / </span>
        <span className="text-red-600 font-bold">{winner.withStrokeLosses} lost</span>
        <span className="text-gray-500"> despite strokes</span>
      </p>

      <p className="text-xs text-gray-500" data-testid="bsu-summary-line">
        Protected {winner.protectedHoles} of {winner.strokesReceived} stroke holes ({ratePct}%)
      </p>
    </section>
  );
}
